import type { TFunction } from 'i18next';
import type SettingPageContext from 'interfaces/SettingPageContext';
import { SettingSectionKeyEnum } from '../../enums/SettingSectionKeyEnum';
import { SettingTypeEnum } from '../../enums/SettingTypeEnum';
import type { SettingItem } from '../../interfaces/SettingItem';
import type { SettingSection } from '../../interfaces/SettingSection';
// Removed translate import as per instruction
import { hasSettingLimitation, slugify } from '../../utils/utils';

export const settingSections: SettingSection[] = [
  {
    key: SettingSectionKeyEnum.Customization,
    items: [
      {
        description: 'settings.companyBranding.description',
        icon: 'BuildingIcon',
        title: 'settings.companyBranding.title',
        to: slugify(SettingTypeEnum.CompanyBranding),
        type: SettingTypeEnum.CompanyBranding,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.CompanyBranding),
      },
      {
        description: 'settings.emailCustomizations.description',
        icon: 'MailIcon',
        title: 'settings.emailCustomizations.title',
        to: slugify(SettingTypeEnum.EmailCustomizations),
        type: SettingTypeEnum.EmailCustomizations,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.EmailCustomizations),
      },
      {
        description: 'settings.outboundEmail.description',
        icon: 'MailPlusIcon',
        title: 'settings.outboundEmail.title',
        to: slugify(SettingTypeEnum.OutboundEmail),
        type: SettingTypeEnum.OutboundEmail,
        // Gated by isCustomEmailDomainEnabled (SuperAdmin + licence) on the auth payload.
        condition: (context) =>
          Boolean(context.authenticatedUser?.isCustomEmailDomainEnabled) &&
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.OutboundEmail),
      },
    ],
    label: 'common.customizations',
  },
  {
    items: [
      {
        description: 'settings.singleSignOn.description',
        icon: 'KeySquareIcon',
        title: 'settings.singleSignOn.title',
        to: slugify(SettingTypeEnum.SingleSignOn),
        type: SettingTypeEnum.SingleSignOn,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.SingleSignOn) &&
          (context.authenticatedUser?.isSSOEnabled ?? false),
      },
      {
        description: 'common.sessionControlDescription',
        icon: 'SessionControlIcon',
        title: 'common.sessionControl',
        to: slugify(SettingTypeEnum.SessionControl),
        type: SettingTypeEnum.SessionControl,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.SessionControl),
      },
      {
        description: 'common.deviceAccessLimitationDescription',
        icon: 'DeviceAccessLimitationIcon',
        title: 'common.deviceAccessLimitation',
        to: slugify(SettingTypeEnum.DeviceAccessLimitation),
        type: SettingTypeEnum.DeviceAccessLimitation,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.SessionControl) && false,
      },
      {
        description: 'settings.ipRegionRestriction.description',
        icon: 'ShieldCheckIcon',
        title: 'settings.ipRegionRestriction.title',
        to: slugify(SettingTypeEnum.IpRegionRestriction),
        type: SettingTypeEnum.IpRegionRestriction,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.IpRegionRestriction),
      },
      {
        description: 'settings.totpRequired.description',
        icon: 'ShieldEllipsisIcon',
        title: 'settings.totpRequired.title',
        to: slugify(SettingTypeEnum.TotpRequired),
        type: SettingTypeEnum.TotpRequired,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.TotpRequired),
      },
      {
        description: 'settings.passkeySettings.description',
        icon: 'KeyIcon',
        title: 'settings.passkeySettings.title',
        to: slugify(SettingTypeEnum.PasskeySettings),
        type: SettingTypeEnum.PasskeySettings,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.PasskeySettings),
      },
      {
        description: 'settings.securityAnalysis.description',
        icon: 'ShieldAlertIcon',
        title: 'settings.securityAnalysis.title',
        to: slugify(SettingTypeEnum.SecurityAnalysis),
        type: SettingTypeEnum.SecurityAnalysis,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.SecurityAnalysis),
      },
      {
        description: 'settings.antivirusScan.description',
        icon: 'BugIcon',
        title: 'settings.antivirusScan.title',
        to: slugify(SettingTypeEnum.AntivirusScan),
        type: SettingTypeEnum.AntivirusScan,
        condition: (context) =>
          (context.authenticatedUser?.isAntivirusEnabled ?? false) &&
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.AntivirusScan),
      },
      {
        description: 'settings.activityLogRetention.description',
        icon: 'HistoryIcon',
        title: 'settings.activityLogRetention.title',
        to: slugify(SettingTypeEnum.ActivityLogRetention),
        type: SettingTypeEnum.ActivityLogRetention,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.ActivityLogRetention),
      },
    ],
    key: SettingSectionKeyEnum.Security,
    label: 'common.security',
  },
  {
    key: SettingSectionKeyEnum.AiGrant,
    items: [
      {
        description: 'settings.aiTagging.description',
        icon: 'TagAiIcon',
        title: 'settings.aiTagging.title',
        to: slugify(SettingTypeEnum.AiTagging),
        type: SettingTypeEnum.AiTagging,
        condition: (context) => {
          return (context.isAiEnabled && context.authenticatedUser?.isAIGrantEnabled) ?? false;
        },
      },
      {
        description: 'settings.aiFileSummary.description',
        icon: 'ScanTextIcon',
        title: 'settings.aiFileSummary.title',
        to: slugify(SettingTypeEnum.AiFileSummary),
        type: SettingTypeEnum.AiFileSummary,
        condition: (context) => {
          return (context.isAiEnabled && context.authenticatedUser?.isAIGrantEnabled) ?? false;
        },
      },
      {
        description: 'settings.aiFileChat.description',
        icon: 'BotMessageSquareIcon',
        title: 'settings.aiFileChat.title',
        to: slugify(SettingTypeEnum.AiFileChat),
        type: SettingTypeEnum.AiFileChat,
        condition: (context) => {
          return (context.isAiEnabled && context.authenticatedUser?.isAIGrantEnabled) ?? false;
        },
      },
      {
        description: 'settings.aiScraping.description',
        icon: 'FileSearchIcon',
        title: 'settings.aiScraping.title',
        to: slugify(SettingTypeEnum.AiScraping),
        type: SettingTypeEnum.AiScraping,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.AiScraping),
      },
    ],
    label: 'AIGrant',
  },
  {
    items: [
      {
        description: 'common.groupCreationControlDescription',
        icon: 'GroupCreationControlIcon',
        title: 'common.groupCreationControl',
        to: slugify(SettingTypeEnum.GroupCreationControl),
        type: SettingTypeEnum.GroupCreationControl,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.GroupCreationControl) &&
          false,
      },
      {
        description: 'common.msOfficeEditingDescription',
        icon: 'MSOfficeControlIcon',
        title: 'common.msOfficeEditing',
        to: slugify(SettingTypeEnum.OnlineEditors),
        type: SettingTypeEnum.OnlineEditors,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.OnlineEditors) && false,
      },
      {
        description: 'common.contributorStorageLimitDescription',
        icon: 'ContributorStorageLimitIcon',
        title: 'common.contributorStorageLimit',
        to: slugify(SettingTypeEnum.ContributorStorageLimit),
        type: SettingTypeEnum.ContributorStorageLimit,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.ContributorStorageLimit) &&
          false,
      },
      {
        description: 'common.certifiedTimestampingDescription',
        icon: 'CertifiedTimestampingIcon',
        title: 'common.certifiedTimestamping',
        to: slugify(SettingTypeEnum.CertifiedTimestamping),
        type: SettingTypeEnum.CertifiedTimestamping,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.CertifiedTimestamping) &&
          false,
      },
      {
        description: 'common.adminRegisteredUserVisibilityDescription',
        icon: 'ShieldUserIcon',
        title: 'common.adminRegisteredUserVisibility',
        to: slugify(SettingTypeEnum.AdminRegisteredMemberVisibility),
        type: SettingTypeEnum.AdminRegisteredMemberVisibility,
        condition: (context) =>
          !hasSettingLimitation(
            context.deniedSettings,
            SettingTypeEnum.AdminRegisteredMemberVisibility,
          ),
      },
    ],
    label: 'common.members',
    key: SettingSectionKeyEnum.Members,
  },
  {
    key: SettingSectionKeyEnum.Features,
    items: [
      {
        description: 'settings.quickShareMode.description',
        icon: 'RabbitIcon',
        title: 'settings.quickShareMode.title',
        to: slugify(SettingTypeEnum.QuickShareMode),
        type: SettingTypeEnum.QuickShareMode,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.QuickShareMode),
      },
    ],
    label: 'common.features',
  },
  {
    key: SettingSectionKeyEnum.Contents,
    items: [
      {
        description: 'common.folderCreationPermissionDescription',
        icon: 'FolderCreationPermissionIcon',
        title: 'common.folderCreationPermission',
        to: slugify(SettingTypeEnum.FolderCreationPermission),
        type: SettingTypeEnum.FolderCreationPermission,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.FolderCreationPermission) &&
          false,
      },
      {
        description: 'common.editorFileManagementDescription',
        icon: 'EditorFileManagementIcon',
        title: 'common.editorFileManagement',
        to: slugify(SettingTypeEnum.EditorFileManagement),
        type: SettingTypeEnum.EditorFileManagement,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.EditorFileManagement) &&
          false,
      },
      {
        description: 'settings.fileTypeRestriction.description',
        icon: 'FileSlidersIcon',
        title: 'settings.fileTypeRestriction.title',
        to: slugify(SettingTypeEnum.FileTypeRestriction),
        type: SettingTypeEnum.FileTypeRestriction,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.FileTypeRestriction),
      },
      {
        description: 'settings.publicAccessLink.description',
        icon: 'LinkIcon',
        title: 'settings.publicAccessLink.title',
        to: slugify(SettingTypeEnum.PublicAccessLink),
        type: SettingTypeEnum.PublicAccessLink,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.PublicAccessLink),
      },
      {
        description: 'common.uploadWithTagDescription',
        icon: 'UploadWithTagIcon',
        title: 'common.uploadWithTag',
        to: slugify(SettingTypeEnum.UploadWithTag),
        type: SettingTypeEnum.UploadWithTag,
        isUnderDevelopment: true,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.UploadWithTag) && false,
      },
      {
        description: 'settings.fileTagManagement.description',
        icon: 'TagsIcon',
        title: 'settings.fileTagManagement.title',
        to: slugify(SettingTypeEnum.FileTagManagement),
        type: SettingTypeEnum.FileTagManagement,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.FileTagManagement),
      },
      {
        description: 'settings.fileTagAdditionRestriction.description',
        icon: 'TagIcon',
        title: 'settings.fileTagAdditionRestriction.title',
        to: slugify(SettingTypeEnum.FileTagAdditionRestriction),
        type: SettingTypeEnum.FileTagAdditionRestriction,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.FileTagAdditionRestriction),
      },
      {
        description: 'settings.fileSalesPermission.description',
        icon: 'StoreIcon',
        title: 'settings.fileSalesPermission.title',
        to: slugify(SettingTypeEnum.FileSalesPermission),
        type: SettingTypeEnum.FileSalesPermission,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.FileSalesPermission) &&
          (context?.authenticatedUser?.isSellEnabled ?? false),
      },
      {
        description: 'settings.watermarkProtection.description',
        icon: 'StampIcon',
        title: 'settings.watermarkProtection.title',
        to: slugify(SettingTypeEnum.WatermarkProtection),
        type: SettingTypeEnum.WatermarkProtection,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.WatermarkProtection),
      },
      {
        description: 'common.fileSizeLimitDescription',
        icon: 'FileUpIcon',
        title: 'common.fileSizeLimit',
        to: slugify(SettingTypeEnum.FileSizeLimit),
        type: SettingTypeEnum.FileSizeLimit,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.FileSizeLimit),
      },
      {
        description: 'settings.fileDeletionRestriction.description',
        icon: 'FileXIcon',
        title: 'settings.fileDeletionRestriction.title',
        to: slugify(SettingTypeEnum.FileDeletionRestriction),
        type: SettingTypeEnum.FileDeletionRestriction,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.CompanyBranding),
      },
      {
        description: 'settings.fileVersioningRetentionPolicies.description',
        icon: 'HistoryIcon',
        title: 'settings.fileVersioningRetentionPolicies.title',
        to: slugify(SettingTypeEnum.FileVersioningRetentionPolicies),
        type: SettingTypeEnum.FileVersioningRetentionPolicies,
        condition: (context) =>
          !hasSettingLimitation(
            context.deniedSettings,
            SettingTypeEnum.FileVersioningRetentionPolicies,
          ),
      },
      {
        description: 'settings.mandatoryFileClassification.description',
        icon: 'ShapesIcon',
        title: 'settings.mandatoryFileClassification.title',
        to: slugify(SettingTypeEnum.MandatoryFileClassification),
        type: SettingTypeEnum.MandatoryFileClassification,
        condition: (context) =>
          !hasSettingLimitation(
            context.deniedSettings,
            SettingTypeEnum.MandatoryFileClassification,
          ),
      },
      {
        description: 'settings.shortcutLink.description',
        icon: 'FileSymlinkIcon',
        title: 'settings.shortcutLink.title',
        to: slugify(SettingTypeEnum.ShortcutLink),
        type: SettingTypeEnum.ShortcutLink,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.ShortcutLink),
      },
      {
        description: 'settings.retentionPolicies.description',
        icon: 'FileAutoDeletionIcon',
        title: 'settings.retentionPolicies.title',
        to: slugify(SettingTypeEnum.RetentionPolicies),
        type: SettingTypeEnum.RetentionPolicies,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.RetentionPolicies) &&
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.TrashRetentionPolicy),
      },
    ],
    label: 'common.contents',
  },
  {
    key: SettingSectionKeyEnum.DataLossPrevention,
    items: [
      {
        description: 'settings.externalFileSharingRestriction.description',
        icon: 'AtSignIcon',
        title: 'settings.externalFileSharingRestriction.title',
        to: slugify(SettingTypeEnum.ExternalFileSharingRestriction),
        type: SettingTypeEnum.ExternalFileSharingRestriction,
        condition: (context) =>
          !hasSettingLimitation(
            context.deniedSettings,
            SettingTypeEnum.ExternalFileSharingRestriction,
          ),
      },
      {
        description: 'settings.downloadRestrictions.description',
        icon: 'FileDownIcon',
        title: 'settings.downloadRestrictions.title',
        to: slugify(SettingTypeEnum.DownloadRestrictions),
        type: SettingTypeEnum.DownloadRestrictions,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.DownloadRestrictions),
      },
      {
        description: 'settings.downloadAllowedTags.description',
        icon: 'BookmarkCheckIcon',
        title: 'settings.downloadAllowedTags.title',
        to: slugify(SettingTypeEnum.DownloadAllowedTags),
        type: SettingTypeEnum.DownloadAllowedTags,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.DownloadAllowedTags),
      },
      {
        description: 'settings.forcedAntiCaptureMode.description',
        icon: 'CameraOffIcon',
        title: 'settings.forcedAntiCaptureMode.title',
        to: slugify(SettingTypeEnum.ForcedAntiCaptureMode),
        type: SettingTypeEnum.ForcedAntiCaptureMode,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.ForcedAntiCaptureMode),
      },
      {
        description: 'settings.resharingPermission.description',
        icon: 'WaypointsIcon',
        title: 'settings.resharingPermission.title',
        to: slugify(SettingTypeEnum.ResharingPermission),
        type: SettingTypeEnum.ResharingPermission,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.ResharingPermission),
      },
      {
        description: 'settings.mandatoryExpirationSharedFile.description',
        icon: 'CalendarClockIcon',
        title: 'settings.mandatoryExpirationSharedFile.title',
        to: slugify(SettingTypeEnum.MandatoryExpirationSharedFile),
        type: SettingTypeEnum.MandatoryExpirationSharedFile,
        condition: (context) =>
          !hasSettingLimitation(
            context.deniedSettings,
            SettingTypeEnum.MandatoryExpirationSharedFile,
          ),
      },
      {
        description: 'settings.secureFileLocalAccess.description',
        icon: 'FileKeyIcon',
        title: 'settings.secureFileLocalAccess.title',
        to: slugify(SettingTypeEnum.SecureFileLocalAccess),
        type: SettingTypeEnum.SecureFileLocalAccess,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.SecureFileLocalAccess) &&
          (context.authenticatedUser?.isRemoteGrantEnabled ?? false),
      },
      {
        description: 'settings.tagBasedAccessControl.description',
        icon: 'TagIcon',
        title: 'settings.tagBasedAccessControl.title',
        to: slugify(SettingTypeEnum.TagBasedAccessControl),
        type: SettingTypeEnum.TagBasedAccessControl,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.TagBasedAccessControl),
      },
      {
        description: 'settings.sharingApprovalRequirement.description',
        icon: 'FileCheckIcon',
        title: 'settings.sharingApprovalRequirement.title',
        to: slugify(SettingTypeEnum.FileSharingApproval),
        type: SettingTypeEnum.FileSharingApproval,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.FileSharingApproval),
      },
      {
        description: 'settings.forcedFileInvisibility.description',
        icon: 'EyeClosedIcon',
        title: 'settings.forcedFileInvisibility.title',
        to: slugify(SettingTypeEnum.ForcedFileInvisibility),
        type: SettingTypeEnum.ForcedFileInvisibility,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.ForcedFileInvisibility),
      },
    ],
    label: 'common.dataLossPrevention',
  },
  {
    key: SettingSectionKeyEnum.Integrations,
    items: [
      {
        description: 'settings.remotegrantForceEncryption.description',
        icon: 'RemotegrantFileIcon',
        title: 'settings.remotegrantForceEncryption.title',
        to: slugify(SettingTypeEnum.RemotegrantForceEncryption),
        type: SettingTypeEnum.RemotegrantForceEncryption,
        condition: (context) =>
          !hasSettingLimitation(
            context.deniedSettings,
            SettingTypeEnum.RemotegrantForceEncryption,
          ) && false,
      },
      {
        description: 'settings.sharePointIntegration.description',
        icon: 'FolderSyncIcon',
        title: 'settings.sharePointIntegration.title',
        to: slugify(SettingTypeEnum.SharePointIntegration),
        type: SettingTypeEnum.SharePointIntegration,
        // Gated by License.IsSharePointGrantAvailable, surfaced as isSharePointGrantEnabled in the
        // auth payload: an absent flag hides the entry rather than half-enabling the feature.
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.SharePointIntegration) &&
          context.authenticatedUser?.isSharePointGrantEnabled === true,
      },
      {
        description: 'settings.activeDirectoryLdap.description',
        icon: 'HardDriveDownloadIcon',
        title: 'settings.activeDirectoryLdap.title',
        to: slugify(SettingTypeEnum.ActiveDirectoryLdap),
        type: SettingTypeEnum.ActiveDirectoryLdap,
        // Gated by isActiveDirectorySyncEnabled on the auth payload (on-premises + licence + admin).
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.ActiveDirectoryLdap) &&
          context.authenticatedUser?.isActiveDirectorySyncEnabled === true,
      },
    ],
    label: 'common.integrations',
  },
  {
    key: SettingSectionKeyEnum.SuperAdmins,
    items: [
      {
        description: 'settings.onBehalfMode.description',
        icon: 'SquareUserIcon',
        title: 'settings.onBehalfMode.title',
        to: slugify(SettingTypeEnum.OnBehalfMode),
        type: SettingTypeEnum.OnBehalfMode,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.OnBehalfMode) && false,
      },
      {
        description: 'settings.adminSettingsPermissions.description',
        icon: 'BrickWallShieldIcon',
        title: 'settings.adminSettingsPermissions.title',
        to: slugify(SettingTypeEnum.AdminSettingsPermissions),
        type: SettingTypeEnum.AdminSettingsPermissions,
        condition: (context) =>
          !hasSettingLimitation(context.deniedSettings, SettingTypeEnum.AdminSettingsPermissions),
      },
    ],
    label: 'common.superAdmins',
  },
];

/**
 * Filter setting sections based on the context
 * @param context - The context of the setting page
 * @returns The filtered setting sections
 */
/** Slug (`email-customizations`) → hub title i18n key (`settings.emailCustomizations.title`). */
const settingTitleKeyBySlug: ReadonlyMap<string, string> = new Map(
  settingSections.flatMap((section) => section.items.map((item) => [item.to, item.title] as const)),
);

/** Title i18n key for a settings route slug, or undefined if unknown. */
export function getSettingTitleKeyBySlug(slug: string): string | undefined {
  return settingTitleKeyBySlug.get(slug);
}

/**
 * Returns the translated title for a setting type by looking it up in settingSections.
 * Used e.g. to display "Used in: [setting titles]" for tags.
 */
export function getSettingTitle(settingType: SettingTypeEnum | string, t: TFunction): string {
  const typeStr = String(settingType);
  for (const section of settingSections) {
    const item = section.items.find((i) => i.type === typeStr);
    if (item) return t(item.title);
  }
  return typeStr;
}

/**
 * Returns the icon name registered for a setting type, or `null` if unknown.
 */
export function getSettingIcon(settingType: SettingTypeEnum | string): SettingItem['icon'] | null {
  const typeStr = String(settingType);
  for (const section of settingSections) {
    const item = section.items.find((i) => i.type === typeStr);
    if (item) return item.icon;
  }
  return null;
}

/**
 * Returns the path to a setting page (e.g. /settings/company-branding).
 * Used to build links to settings when showing "user referenced in settings" errors.
 */
export function getSettingPath(settingType: SettingTypeEnum | string): string {
  const typeStr = String(settingType);
  for (const section of settingSections) {
    const item = section.items.find((i) => i.type === typeStr);
    if (item) return `/settings/${item.to}`;
  }
  return `/settings/${slugify(typeStr)}`;
}

export function filteredSettingSections(context: SettingPageContext): SettingSection[] {
  return settingSections
    .map((section) => {
      // Filter items that satisfy their condition and are not denied
      const visibleItems = section.items.filter((item) => {
        // Check if setting is denied
        if (hasSettingLimitation(context.deniedSettings, item.type)) {
          return false;
        }
        // Check condition if present
        return !item.condition || item.condition(context);
      });
      // Return the section only if it has visible items
      return visibleItems.length > 0 ? { ...section, items: visibleItems } : null;
    })
    .filter((section): section is SettingSection => section !== null);
}
